import React from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  DomainAddOutlined,
  LocationOnOutlined,
  PhoneOutlined,
  CategoryOutlined,
  CodeOutlined,
  PinDropOutlined,
  MapOutlined,
  PlaceOutlined,
  BusinessOutlined,
  ApartmentOutlined,
  CheckCircleOutline,
  CancelOutlined,
  CalendarTodayOutlined,
  AccountBox,
  HomeWork,
  WarehouseOutlined,
} from "@mui/icons-material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

import "leaflet/dist/leaflet.css";
import L from "leaflet";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIconRetina from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useBranchDetailContext } from "contexts/BranchDetailContext";
import { Link } from "react-router-dom";

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

const BranchBasicDetails = () => {
  const { branchDetail, loadingDetail } = useBranchDetailContext();

  return !loadingDetail ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item md={12}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Grid container spacing={2}>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <DomainAddOutlined color="primary" />}
                  title="Branch Name"
                  subtitle={<Box>{branchDetail?.name || "Not Available"}</Box>}
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <DomainAddOutlined color="primary" />}
                  title="Branch Code"
                  subtitle={<Box>{branchDetail?.code || "Not Available"}</Box>}
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <LocationOnOutlined color="primary" />}
                  title="Address"
                  subtitle={
                    <Box
                      sx={{
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {branchDetail?.address || "Not Available"}
                    </Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <WarehouseOutlined color="primary" />}
                  title="Warehouse"
                  subtitle={
                    <Box>
                      {branchDetail?.warehouse?.name ? (
                        <Tooltip title={"View Warehouse"} arrow>
                          <Link
                            to={`/app/crm/warehouse/${branchDetail?.warehouse?._id}/detail`}
                            className="text-inherit underline"
                          >
                            {branchDetail.warehouse.name}
                          </Link>
                        </Tooltip>
                      ) : (
                        "Not Available"
                      )}
                    </Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <PhoneOutlined color="primary" />}
                  title="Phone"
                  subtitle={branchDetail?.phone || "Not Available"}
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <AccountBox color="primary" />}
                  title="Phone"
                  subtitle={
                    branchDetail?.branchManager?.name || "Not Available"
                  }
                />
              </Grid>

              <Grid item md={3}>
                <InformationTile
                  icon={() => <CategoryOutlined color="primary" />}
                  title="Can Address"
                  subtitle={
                    <Box
                      sx={{
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {branchDetail?.canAddress?.name || "Not Available"}
                    </Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <CodeOutlined color="primary" />}
                  title="Address Code"
                  subtitle={
                    <Box>
                      {branchDetail?.canAddress?.code || "Not Available"}
                    </Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <PinDropOutlined color="primary" />}
                  title="Coordinates"
                  subtitle={
                    <Box>
                      {branchDetail?.canAddress?.coordinates
                        ? `${branchDetail.canAddress.coordinates.lat}, ${branchDetail.canAddress.coordinates.lng}`
                        : "No coordinates available"}
                    </Box>
                  }
                />
              </Grid>

              <Grid item md={3}>
                <InformationTile
                  icon={() => <MapOutlined color="primary" />}
                  title="Areas Covered"
                  subtitle={
                    <Box>{branchDetail?.areasCovered || "Not Available"}</Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <PlaceOutlined color="primary" />}
                  title="Region"
                  subtitle={
                    <Box>{branchDetail?.region?.name || "Not Available"}</Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <BusinessOutlined color="primary" />}
                  title="District"
                  subtitle={
                    <Box>{branchDetail?.district?.name || "Not Available"}</Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <ApartmentOutlined color="primary" />}
                  title="Municipality"
                  subtitle={
                    <Box>
                      {branchDetail?.municipality?.name || "Not Available"}
                    </Box>
                  }
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <HomeWork color="primary" />}
                  title="Parent Branch"
                  subtitle={
                    <Box>
                      {branchDetail?.parentBranch ? (
                        <Tooltip title={"View Parent Branch"} arrow>
                          <Link
                            to={`/app/crm/branch/${branchDetail?.parentBranch?._id}/detail`}
                            className="text-inherit underline"
                          >
                            {branchDetail.parentBranch.name}
                          </Link>
                        </Tooltip>
                      ) : (
                        "Not Available"
                      )}
                    </Box>
                  }
                />
              </Grid>

              <Grid item md={3}>
                <InformationTile
                  icon={() =>
                    branchDetail?.status === "Active" ? (
                      <CheckCircleOutline color="primary" />
                    ) : (
                      <CancelOutlined color="primary" />
                    )
                  }
                  title="Status"
                >
                  <div
                    className={`uppercase ${
                      branchDetail?.status === "Active"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {branchDetail?.status || "Not Available"}
                  </div>
                </InformationTile>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item md={6}>
          {branchDetail?.coordinates?.lat && branchDetail?.coordinates?.long ? (
            <MapContainer
              center={[
                branchDetail.coordinates.lat,
                branchDetail.coordinates.long,
              ]}
              zoom={18}
              style={{ width: "100%", height: 410 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker
                position={[
                  branchDetail.coordinates.lat,
                  branchDetail.coordinates.long,
                ]}
              >
                <Popup>Main Coordinates Location</Popup>
              </Marker>
            </MapContainer>
          ) : branchDetail?.canAddress?.coordinates?.lat &&
            branchDetail?.canAddress?.coordinates?.lng ? (
            <MapContainer
              center={[
                branchDetail.canAddress.coordinates.lat,
                branchDetail.canAddress.coordinates.lng,
              ]}
              zoom={8}
              style={{ width: "100%", height: 410 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker
                position={[
                  branchDetail.canAddress.coordinates.lat,
                  branchDetail.canAddress.coordinates.lng,
                ]}
              >
                <Popup offset={[-15, -25]}>Branch Location</Popup>
              </Marker>
            </MapContainer>
          ) : (
            <Typography>No location to show</Typography>
          )}
        </Grid>
        <Grid item xs={6} sm={6} md={6}>
          <Box display="flex" flexDirection="column" gap={1}>
            <InformationTile
              icon={() => <CalendarTodayOutlined color="primary" />}
              title="Working Hours"
              subtitle={
                branchDetail?.workingHours?.length ? (
                  <Box sx={{ overflowX: "auto", maxWidth: "90%" }}>
                    <Table sx={{ minWidth: 300 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            sx={{
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.8rem", sm: "1rem" },
                              fontWeight: "bold",
                            }}
                          >
                            Day
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.8rem", sm: "1rem" },
                              fontWeight: "bold",
                            }}
                          >
                            Working Hours
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {branchDetail.workingHours.map(
                          ({ day, startsAt, closesAt }) => {
                            const isClosed = !startsAt && !closesAt;
                            const displayTime = isClosed
                              ? "Closed"
                              : `${startsAt || "N/A"} - ${closesAt || "N/A"}`;

                            return (
                              <TableRow key={day}>
                                <TableCell
                                  align="center"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "1rem",
                                    },
                                  }}
                                >
                                  {day}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    maxWidth: 200,
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "1rem",
                                    },
                                  }}
                                >
                                  {displayTime}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  "Not Available"
                )
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default BranchBasicDetails;
